import enquire from 'enquire.js'

import Component from '../core/Component'
import { queries } from '../core/config'
import Lightpick from '../plugins/lightpick'
import Scroll from '../services/Scroll'

export const STATES = {
    SELECTED: 'is-selected'
}

const MONTHS = {
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',],
    cs: ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec',],
    de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',]
}

const DAYS = {
    en: {
        one: 'day',
        other: 'days'
    },
    cs: {
        one: 'den',
        few: 'dny',
        many: 'dní'
    },
    de: {
        one: 'tag',
        other: 'tage'
    },
}

const NIGHTS = {
    en: {
        one: 'night',
        other: 'days'
    },
    cs: {
        one: 'noc',
        few: 'noci',
        many: 'nocí'
    },
    de: {
        one: 'nacht',
        other: 'nächte'
    }
}

export default class DatePicker extends Component {
    constructor(element, options) {
        super(element, options)

        this.ref = {
            container: null,
            indicator: null,
            from: null,
            to: null,
            toggleFrom: null,
            toggleTo: null
        }

        this.isOpen = false
        this.currentEnd

        this.defaultFrom = (this.ref.toggleFrom.innerHTML) ? this.ref.toggleFrom.innerHTML : 'Check in'
        this.defaultTo = (this.ref.toggleTo.innerHTML) ? this.ref.toggleTo.innerHTML : 'Check out'
        this.lang = (this.element.dataset.lang) ? this.element.dataset.lang : 'cs'

        this.lightpickOptions = {
            parentEl: this.ref.container,
            inline: true,
            field: this.ref.from,
            secondField: this.ref.to,
            lang: this.lang,
            singleDate: false,
            numberOfMonths: 2,
            orientation: 'right',
            minDate: new Date(),
            locale: {
                months: MONTHS[this.lang],
                tooltip: this.options.tooltipNights ? NIGHTS[this.lang] : DAYS[this.lang],
                pluralize: (i, locale) => {
                    if ('one' in locale && i % 10 === 1 && !(i % 100 === 11)) return locale.one
                    if ('few' in locale && i % 10 === Math.floor(i % 10) && i % 10 >= 2 && i % 10 <= 4 && !(i % 100 >= 12 && i % 100 <= 14)) return locale.few
                    if ('many' in locale && (i % 10 === 0 || i % 10 === Math.floor(i % 10) && i % 10 >= 5 && i % 10 <= 9 || i % 100 === Math.floor(i % 100) && i % 100 >= 11 && i % 100 <= 14)) return locale.many
                    if ('other' in locale) return locale.other

                    return ''
                }
            },
            onSelect: this.handleSelect,
            ...this.options
        }

        this.enquireHandler = {
            match: () => this.match(),
            unmatch: () => this.unmatch()
        }
    }

    prepare() {
        this.ref.toggleFrom.addEventListener('click', this.handleClick)
        this.ref.toggleTo.addEventListener('click', this.handleClick)

        this.datepicker = new Lightpick(this.lightpickOptions)

        document.addEventListener('click', this.handleOuterClick)
        Scroll.on('resize', this.handleResize)
        enquire.register(queries.mediumMax, this.enquireHandler)

        this.handleResize()
    }

    destroy() {
        this.ref.toggleFrom.removeEventListener('click', this.handleClick)
        this.ref.toggleTo.removeEventListener('click', this.handleClick)

        document.removeEventListener('click', this.handleOuterClick)
        Scroll.off('resize', this.handleResize)
        enquire.unregister(queries.mediumMax, this.enquireHandler)
    }

    match = () => {
        this.datepicker.reloadOptions({numberOfMonths: 1, numberOfColumns: 1})
    }

    unmatch = () => {
        this.datepicker.reloadOptions({numberOfMonths: 2, numberOfColumns: 2})
    }

    handleSelect = (start, end) => {
        if (end != this.currentEnd) {
            this.close()
        }

        let localStart = start ? start.toDate().toLocaleString(this.lang, { timeZone: 'UTC', day: 'numeric', month: 'long', year: '2-digit' }) : false
        let localEnd = end ? end.toDate().toLocaleString(this.lang, { timeZone: 'UTC', day: 'numeric', month: 'long', year: '2-digit'  }) : false

        this.ref.toggleFrom.innerHTML = localStart ? localStart : this.defaultFrom
        this.ref.toggleTo.innerHTML = localEnd ? localEnd : this.defaultTo

        this.ref.from.value = start ? start.format('YYYY-MM-DD') : ''
        this.ref.to.value = end ? end.format('YYYY-MM-DD') : ''

        this.currentEnd = end

        this.element.classList.add(STATES.SELECTED)

        this.handleResize()
        this.moveIndicator()
    }

    handleOuterClick = event => {
        if (!this.element.contains(event.target)) {
            this.close()
        }
    }

    handleClick = (event) => {
        event.preventDefault()

        if (this.isOpen) {
            this.close()
        } else {
            this.open()
            this.moveIndicator()
        }
    }

    handleResize = () => {
        let lightpick = this.element.querySelector('section.lightpick')

        this.height = lightpick.getBoundingClientRect().height + 60
        this.width = lightpick.getBoundingClientRect().width
        this.top = this.ref.toggleTo.getBoundingClientRect().bottom - this.element.getBoundingClientRect().top
        this.ref.from.left = this.ref.from.getBoundingClientRect().left + this.ref.from.getBoundingClientRect().width/2 - this.element.getBoundingClientRect().left
        this.ref.to.left = this.ref.to.getBoundingClientRect().left + this.ref.to.getBoundingClientRect().width/2 - this.element.getBoundingClientRect().left
        this.element.width = this.element.getBoundingClientRect().width
        this.ref.container.style.top = this.top + 'px'
    }

    open() {
        this.ref.container.style.maxHeight = this.height + 'px'
        this.isOpen = true
    }

    close() {
        this.ref.container.style.maxHeight = 0 + 'px'
        this.isOpen = false
    }

    moveIndicator() {
        if (this.ref.from.value == "") {
            this.ref.indicator.style.left = this.ref.from.left + this.width - this.element.width + 'px'
            this.ref.indicator.style.top = '0px'
        } else if (this.ref.to.value == "") {
            this.ref.indicator.style.left = this.ref.to.left + this.width - this.element.width + 'px'
            this.ref.indicator.style.top = '0px'
        } else {
            this.ref.indicator.style.top = '8px'
        }
    }
}
